import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Loading from "../common/Loading";
import Dropdown from "./Dropdown";
import { ACCENT, Field, inputClass, ringStyle } from "./shared";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";
import { WALLET_MESSAGES, walletApi, walletErrorMessage } from "./walletApi";
import { formatDate, formatDateTime, formatMoney } from "./walletFormat";

// Artist Studio > Music Wallet > Payout method.
//  - The bank account is never typed into Pipaan. "Change payout method" first asks the artist to prove it is them (password or a
//    code emailed to them), then opens Stripe's own page. Pipaan keeps only the Stripe account id and the last four digits.
//  - When the artist comes back from Stripe, the server reads the Stripe account again: the page never trusts the redirect.
//  - Schedule and threshold are saved with the Save button, checked while choosing and again by the server.
//  - In a test environment (WALLET_PAYOUT_SIMULATION) Stripe's page is replaced by a small dialog that plays its part.

const overlay = "fixed inset-0 z-50 flex [align-items:safe_center] justify-center overflow-y-auto bg-black/40 px-4 py-4";
const UNCHANGED = "Your payout method hasn't been changed.";
const SAVED = "Your payout settings have been saved.";

const STATUS_PILL = {
  connected: "bg-green-100 text-green-800",
  setup_required: "bg-amber-100 text-amber-800",
  restricted: "bg-red-100 text-red-700",
  not_connected: "bg-gray-100 text-gray-700",
};

function useEscape(onClose, disabled) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !disabled) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, disabled]);
}

export const MESSAGE_CLASS = {
  success: "border-green-200 bg-green-50 text-green-900",
  info: "border-gray-200 bg-white text-gray-800",
  error: "border-red-200 bg-red-50 text-red-800",
};

// "Choose a threshold of at least 25.00" (the same sentence the server uses).
export function thresholdError(valueMinor, settings) {
  if (!settings) return "";
  const min = settings.threshold.platform_minimum_minor;
  const allowed = settings.threshold.options_minor || [];
  if (!Number.isSafeInteger(valueMinor) || valueMinor < min || !allowed.includes(valueMinor)) {
    return `Choose a threshold of at least ${formatMoney(min, settings.currency)}`;
  }
  return "";
}

// Step 1 of a method change: password or emailed code. Answers onGranted(grant) once the server has accepted it.
function ReauthModal({ settings, onClose, onGranted }) {
  const canPassword = settings.reauth.password_available;
  const [mode, setMode] = useState(canPassword ? "password" : "otp");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [touched, setTouched] = useState(false);
  const [sent, setSent] = useState(false);
  const [testOtp, setTestOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submitting = useRef(false);
  useEscape(onClose, busy);

  const fieldError = mode === "password"
    ? (!password ? "Enter your password." : "")
    : (otp.length !== 6 ? "Enter the 6-digit code we sent you." : "");
  const shown = touched ? fieldError : "";

  const sendCode = async () => {
    if (sending) return;
    setSending(true);
    setError("");
    try {
      const out = await walletApi.sendPayoutOtp();
      setSent(true);
      setTestOtp(out?.test_otp || "");
    } catch (e) {
      setError(walletErrorMessage(e, WALLET_MESSAGES.otpSend));
    } finally {
      setSending(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (fieldError || submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      const out = await walletApi.reauthPayoutMethod(mode === "password" ? { password } : { otp });
      onGranted(out.grant);
    } catch (err) {
      setError(walletErrorMessage(err, WALLET_MESSAGES.reauth));
      submitting.current = false;
      setBusy(false);
    }
  };

  const switchTo = (next) => { if (busy) return; setMode(next); setTouched(false); setError(""); };

  return (
    <div className={overlay} role="dialog" aria-modal="true" aria-labelledby="reauth-title" data-testid="reauth-modal">
      <form onSubmit={submit} noValidate className="my-8 w-full max-w-[460px] rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <h2 id="reauth-title" className="text-[22px] font-bold text-gray-900">Confirm it's you</h2>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close" className="text-2xl leading-none text-gray-500 disabled:opacity-50" data-testid="reauth-close">×</button>
        </div>
        <p className="mt-1 text-[15px] text-gray-600">For your security, we need to check it's you before you change where your money goes.</p>

        {mode === "password" ? (
          <div className="mt-5">
            <Field label="Password" error={shown}>
              <input
                id="reauth-password" type="password" autoComplete="current-password" value={password} disabled={busy} autoFocus
                onChange={(e) => { setPassword(e.target.value); setTouched(true); setError(""); }} onBlur={() => setTouched(true)}
                aria-invalid={Boolean(shown)} className={inputClass(Boolean(shown))} style={ringStyle} data-testid="reauth-password"
              />
            </Field>
          </div>
        ) : (
          <div className="mt-5">
            {!sent ? (
              <div>
                <p className="text-[15px] text-gray-700">
                  We'll email a 6-digit code{settings.reauth.email_hint ? <> to <span className="font-semibold">{settings.reauth.email_hint}</span></> : ""}. It works for {settings.reauth.otp_minutes} minutes.
                </p>
                <button
                  type="button" onClick={sendCode} disabled={sending}
                  className="mt-3 rounded-full border border-gray-200 bg-white px-6 py-2.5 text-[15px] font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-50" data-testid="reauth-send"
                >
                  {sending ? "Sending…" : "Send code"}
                </button>
              </div>
            ) : (
              <div>
                <Field label="Code" error={shown} hint={`We sent a code to ${settings.reauth.email_hint || "your email"}.`}>
                  <input
                    id="reauth-otp" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={otp} disabled={busy} autoFocus
                    onChange={(e) => { setOtp(e.target.value.replace(/\D/g, "").slice(0, 6)); setTouched(true); setError(""); }} onBlur={() => setTouched(true)}
                    aria-invalid={Boolean(shown)} className={inputClass(Boolean(shown))} style={ringStyle} data-testid="reauth-otp"
                  />
                </Field>
                {testOtp && <p className="mt-1 text-xs text-gray-500" data-testid="reauth-test-otp">Test environment code: {testOtp}</p>}
                <button type="button" onClick={sendCode} disabled={sending || busy} className="mt-2 text-sm font-semibold disabled:opacity-50" style={{ color: ACCENT }} data-testid="reauth-resend">
                  {sending ? "Sending…" : "Send a new code"}
                </button>
              </div>
            )}
          </div>
        )}

        {canPassword && (
          <button type="button" onClick={() => switchTo(mode === "password" ? "otp" : "password")} disabled={busy} className="mt-3 block text-sm font-semibold disabled:opacity-50" style={{ color: ACCENT }} data-testid="reauth-switch">
            {mode === "password" ? "Email me a code instead" : "Use my password instead"}
          </button>
        )}

        {error && <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert" data-testid="reauth-error">{error}</p>}

        <button
          type="submit" disabled={busy || (mode === "otp" && !sent)} data-testid="reauth-submit"
          className="mt-5 w-full rounded-full py-3.5 text-[15px] font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: ACCENT }}
        >
          {busy ? "Checking…" : "Continue"}
        </button>
      </form>
    </div>
  );
}

// Test environments only: plays the part of Stripe's page so the whole flow can be tried without a Stripe account.
function SimulatedStripe({ onConnect, onCancel, busy, error }) {
  const [last4, setLast4] = useState("");
  const [touched, setTouched] = useState(false);
  useEscape(onCancel, busy);
  const problem = /^\d{4}$/.test(last4) ? "" : "Enter the last 4 digits of the test bank account.";
  const shown = touched ? problem : "";
  return (
    <div className={overlay} role="dialog" aria-modal="true" aria-labelledby="sim-title" data-testid="stripe-simulated">
      <form
        noValidate className="my-8 w-full max-w-[460px] rounded-2xl bg-white p-6 shadow-xl"
        onSubmit={(e) => { e.preventDefault(); setTouched(true); if (!problem) onConnect(last4); }}
      >
        <h2 id="sim-title" className="text-[22px] font-bold text-gray-900">Stripe (test mode)</h2>
        <p className="mt-1 text-[15px] text-gray-600">This stands in for Stripe's page in a test environment. No real bank details are used.</p>
        <div className="mt-5">
          <Field label="Last 4 digits of the bank account" error={shown}>
            <input
              id="sim-last4" type="text" inputMode="numeric" maxLength={4} autoComplete="off" value={last4} disabled={busy} autoFocus
              onChange={(e) => { setLast4(e.target.value.replace(/\D/g, "").slice(0, 4)); setTouched(true); }} onBlur={() => setTouched(true)}
              aria-invalid={Boolean(shown)} className={inputClass(Boolean(shown))} style={ringStyle} data-testid="sim-last4"
            />
          </Field>
        </div>
        {error && <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert" data-testid="sim-error">{error}</p>}
        <div className="mt-5 flex gap-3">
          <button type="button" onClick={onCancel} disabled={busy} className="flex-1 rounded-full border border-gray-200 bg-white py-3 text-[15px] font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-50" data-testid="sim-cancel">Cancel</button>
          <button type="submit" disabled={busy} className="flex-1 rounded-full py-3 text-[15px] font-semibold text-white disabled:opacity-50" style={{ backgroundColor: ACCENT }} data-testid="sim-connect">{busy ? "Connecting…" : "Save bank account"}</button>
        </div>
      </form>
    </div>
  );
}

function AccountCard({ settings, onChange, busy }) {
  const a = settings.account;
  return (
    <section className="rounded-xl border border-gray-200 bg-white px-5 py-4" data-testid="stripe-account">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900">Stripe account</p>
          <p className="mt-1.5 text-[15px] text-gray-900" data-testid="account-bank">{a.masked || "No bank account connected"}</p>
          {a.country_name && <p className="mt-0.5 text-sm text-gray-500" data-testid="account-country">{a.country_name}</p>}
        </div>
        <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${STATUS_PILL[a.status] || STATUS_PILL.not_connected}`} data-testid="account-status">{a.status_label}</span>
      </div>
      {settings.change_blocked_reason && <p className="mt-3 text-sm text-amber-800" role="alert" data-testid="account-blocked">{settings.change_blocked_reason}</p>}
      <button
        type="button" onClick={onChange} disabled={busy || Boolean(settings.change_blocked_reason)} data-testid="change-method"
        className="mt-4 rounded-full border border-gray-200 bg-white px-6 py-2.5 text-[15px] font-semibold text-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {a.connected ? "Change payout method" : "Add payout method"}
      </button>
    </section>
  );
}

function PayoutMethodBody() {
  const [params, setParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [message, setMessage] = useState(null); // { tone, text }
  const [schedule, setSchedule] = useState("");
  const [threshold, setThreshold] = useState(0);
  const [saving, setSaving] = useState(false);
  const [reauth, setReauth] = useState(false);
  const [starting, setStarting] = useState(false);
  const [sim, setSim] = useState(false);
  const [simBusy, setSimBusy] = useState(false);
  const [simError, setSimError] = useState("");
  const savingRef = useRef(false);
  const startingRef = useRef(false);
  const returned = useRef(false);

  const adopt = useCallback((s) => {
    setSettings(s);
    setSchedule(s.schedule.value);
    setThreshold(s.threshold.value_minor);
  }, []);

  const load = useCallback(async () => {
    setLoadError("");
    try {
      adopt(await walletApi.payoutSettings());
    } catch (e) {
      setLoadError(walletErrorMessage(e, WALLET_MESSAGES.payoutSettingsLoad));
    }
  }, [adopt]);

  // Coming back from Stripe (?stripe=return, or ?stripe=refresh when its page expired): ask the server what Stripe now says.
  useEffect(() => {
    const back = params.get("stripe");
    if (back && !returned.current) {
      returned.current = true;
      setParams({}, { replace: true });
      (async () => {
        try {
          const out = await walletApi.verifyPayoutMethodChange({});
          adopt(out.settings);
          setMessage({ tone: out.changed ? "success" : "info", text: out.message || UNCHANGED });
        } catch (e) {
          setMessage({ tone: "error", text: walletErrorMessage(e, WALLET_MESSAGES.payoutVerify) });
          load();
        }
      })();
    } else if (!back && !returned.current) {
      load();
    }
  }, [params, setParams, adopt, load]);

  const finishChange = async (body) => {
    try {
      const out = await walletApi.verifyPayoutMethodChange(body);
      adopt(out.settings);
      setSim(false);
      setMessage({ tone: out.changed ? "success" : "info", text: out.message || UNCHANGED });
    } catch (e) {
      setSimError(walletErrorMessage(e, WALLET_MESSAGES.payoutVerify));
    } finally {
      setSimBusy(false);
    }
  };

  const onGranted = async (grant) => {
    if (startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    setMessage(null);
    try {
      const out = await walletApi.startPayoutMethodChange(grant);
      setReauth(false);
      if (out?.url) { window.location.assign(out.url); return; }
      setSimError("");
      setSim(true);
    } catch (e) {
      setReauth(false);
      setMessage({ tone: "error", text: walletErrorMessage(e, WALLET_MESSAGES.accountLink) });
    } finally {
      startingRef.current = false;
      setStarting(false);
    }
  };

  if (loadError && !settings) {
    return (
      <p className="text-gray-700" role="alert" data-testid="payout-method-error">
        {loadError} <button type="button" className="underline font-semibold" onClick={load}>Try again</button>
      </p>
    );
  }
  if (!settings) return <div className="flex justify-center py-24"><Loading /></div>;

  const cur = settings.currency;
  const thresholdProblem = thresholdError(threshold, settings);
  const dirty = schedule !== settings.schedule.value || threshold !== settings.threshold.value_minor;
  const canSave = dirty && !thresholdProblem && !saving;
  const scheduleOptions = settings.schedule.options.map((o) => ({ value: o.value, label: o.label }));
  const chosen = settings.schedule.options.find((o) => o.value === schedule);
  const effective = settings.schedule.options.find((o) => o.value === settings.schedule.effective);
  const thresholdOptions = settings.threshold.options_minor.map((v) => ({ value: v, label: formatMoney(v, cur) }));

  const save = async () => {
    if (!canSave || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setMessage(null);
    try {
      const out = await walletApi.savePayoutSettings({ schedule, minimum_threshold_minor: threshold });
      adopt(out.settings);
      setMessage({ tone: "success", text: SAVED });
    } catch (e) {
      setMessage({ tone: "error", text: walletErrorMessage(e, WALLET_MESSAGES.payoutSettingsSave) });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div data-testid="payout-method">
      <Link to="/artist-studio/wallet" className="text-sm font-semibold text-gray-700 hover:underline" data-testid="back-to-wallet">← Back to Music Wallet</Link>
      <h1 className="mt-4 text-[32px] font-bold leading-tight text-gray-900">Payout method</h1>

      <div className="mt-6 max-w-[490px]">
        {message && (
          <p className={`mb-5 rounded-xl border px-4 py-3 text-sm ${MESSAGE_CLASS[message.tone]}`} role={message.tone === "error" ? "alert" : "status"} data-testid="payout-message">{message.text}</p>
        )}

        {settings.hold.active && (
          <p className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="hold-notice">
            For your security, payouts are on hold for {settings.hold.hours} hours after a payout method change. Withdrawals and scheduled payouts resume {formatDateTime(settings.hold.until)}.
          </p>
        )}

        <AccountCard settings={settings} onChange={() => { setMessage(null); setReauth(true); }} busy={starting} />

        <div className="mt-6">
          <Field label="Payout schedule" hint={chosen ? `${chosen.day}. A change applies from the next cycle.` : undefined}>
            <Dropdown id="payout-schedule" ariaLabel="Payout schedule" value={schedule} options={scheduleOptions} disabled={saving} onChange={(v) => { setSchedule(v); setMessage(null); }} />
          </Field>
          {settings.schedule.applies_from && effective && (
            <p className="mt-1 text-sm text-gray-500" data-testid="schedule-pending">
              Your new schedule starts {formatDate(settings.schedule.applies_from)}. Until then payouts stay {effective.label.toLowerCase()}.
            </p>
          )}
        </div>

        <div className="mt-5">
          <Field
            label="Minimum payout threshold" error={thresholdProblem}
            hint={`A payout is made when your available balance reaches this amount. Minimum ${formatMoney(settings.threshold.platform_minimum_minor, cur)}.`}
          >
            <Dropdown id="payout-threshold" ariaLabel="Minimum payout threshold" value={threshold} options={thresholdOptions} hasError={Boolean(thresholdProblem)} disabled={saving} onChange={(v) => { setThreshold(v); setMessage(null); }} />
          </Field>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button" onClick={save} disabled={!canSave} data-testid="save-payout-settings"
            className="rounded-full px-9 py-3 text-[15px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50" style={{ backgroundColor: ACCENT }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {reauth && <ReauthModal settings={settings} onClose={() => setReauth(false)} onGranted={onGranted} />}
      {sim && (
        <SimulatedStripe
          busy={simBusy} error={simError}
          onConnect={(last4) => { if (simBusy) return; setSimBusy(true); setSimError(""); finishChange({ simulated_last4: last4 }); }}
          onCancel={() => { if (simBusy) return; setSimBusy(true); setSimError(""); finishChange({ cancelled: true }); }}
        />
      )}
    </div>
  );
}

export default function PayoutMethod() {
  const account = useStudioAccount();
  return (
    <StudioGate account={account} active="wallet" heading="" tabs={[]} maxWidth="max-w-4xl">
      <PayoutMethodBody />
    </StudioGate>
  );
}
